import { Device } from '@gumtree/shared/src/types/client-data';
import {
    addCategoryToDataLayer,
    addLocationToDataLayer,
    getTheLatestDataLayerObject,
    updatePageTypeOnDataLayer,
} from '@gumtree/shared/src/util/data-layer-service';
import { generatePageViewPayload } from '@gumtree/shared/src/util/permutive/transform';
import { trackGA4Event, trackGA4PreNav } from '@gumtree/shared/src/util/ga4-shared';
import { keys } from '@gumtree/shared/src/model/generic.model';
import { PricingMetadata, FeatureType } from '@gumtree/shared/src/types/syi';
import { getGrowthbookClientSide } from '@gumtree/shell/src/growthbook/init-growthbook';
import {
    getPageStateThunk,
    Skill,
    updateSyi,
    type SyiModelStatus,
    updateSyiPhoneVerification,
    GROWTHBOOK_VALUE_SYI_TITLE_FIRST,
    GROWTHBOOK_VALUE_SYI_PHOTO_FIRST,
    getTrackLogOnCookie,
} from '../reducers/syi';
import {
    type AttributeKey,
    type BaseSyiResponse,
    dtlSyi,
    FetchError,
    fetchOrThrow,
    helperFactory,
    type PanelFormData,
    type SyiSellerType,
} from './dtl-syi';
import { getPostAdPageType } from './post-ad-page-type-service';
import { L1CategoryIds } from '@gumtree/shared/src/types/categories';
import { removeFalsyProperties } from '@gumtree/shared/src/util/object-helpers';
import { GrowthBookServerSideFeatures } from '@gumtree/shared/src/model/experiment';
import { getCookieValue, tracklogKeyOnSyi } from '@gumtree/shared/src/util/ga4-postponer';

export class SyiModel {
    attrs!: SyiModelAttrs;

    contentType = 'application/json; charset=utf-8' as const;

    /** The advert data we initially retrieve and then POST to the Backend. */
    data = {} as PanelFormData;
    /** The initially retrieved value of @see data */
    initialData = {} as PanelFormData;

    constructor(
        /** e.g. http://dev.gumtree.com:8181/postad/bb3fd6a0bf0343fc88a1e7c884eb0540 */
        public postadURL: string,
        public initialFetchURL: string,
        public saveFetchURL: string,
        public bffBumpUpBaseUrl: string,
        public sellerBaseUrl: string,
        public accountId: number,
        public sellerUrl: string,
        public bffPhoneVerificationUrl: string,
        public device: Device,
        public isOnAiCollectPage: boolean,
        public bffUrl: string
    ) {}

    async initialFetch() {
        const initialData = await fetchOrThrow(
            this.initialFetchURL,
            {
                method: 'GET',
                credentials: 'include', // For cross-origin Seller Request
            },
            {
                serviceName: 'seller',
                path: '/postad/:id',
                method: 'GET',
            }
        ).then((x) => x.json());

        this.initialData = initialData as PanelFormData;

        const data = await this.save(initialData, 'initialFetch');

        if (data?.categoryCrumb) {
            const { categoryCrumb } = data;
            addCategoryToDataLayer(window.dataLayer, categoryCrumb);
        }

        if (data?.locationCrumb) {
            const { locationCrumb } = data;
            addLocationToDataLayer(window.dataLayer, locationCrumb);
        }

        if (data?.pricingMetadata) {
            const { pricingMetadata } = data;
            const postAdPageType = getPostAdPageType(pricingMetadata);
            updatePageTypeOnDataLayer(window.dataLayer, postAdPageType);
        }

        const permutiveOptions = generatePageViewPayload({
            dataLayerBase: getTheLatestDataLayerObject(window.dataLayer),
            additional: { deviceSize: this.device, user: { loggedIn: data?.userLoggedIn! } },
        });

        window.permutive?.addon('web', {
            page: JSON.parse(JSON.stringify(permutiveOptions, null, '\t')),
        });

        if (L1CategoryIds.SERVICES === data?.categoryCrumb?.[0]?.id && initialData.categoryId) {
            const { categoryId } = initialData;
            this.updateSellerSkills(categoryId);
        }
    }

    resetData(data: Record<string, any>) {
        Object.assign(this.data, data);
    }

    handleError(e: any) {
        if (e instanceof FetchError && e.status === 403) {
            location.reload();
        } else {
            throw e;
        }
    }

    async save(
        data: Record<string, any>,
        type:
            | 'initialFetch'
            | 'saveDraft'
            | 'submitForm'
            | 'saveOnAttributeStep'
            | 'saveCategoryOnAttributeStep'
    ) {
        try {
            const appVersion = localStorage.getItem('appVersion') || '';
            data.extendFields = {
                appVersion,
                ...(data.extendFields || {}),
            };
            if (type === 'initialFetch' && this.isOnAiCollectPage) {
                data.attributeStep = true;
                data.aiFlowType =
                    !!getGrowthbookClientSide() &&
                    getGrowthbookClientSide().getFeatureValue(
                        GrowthBookServerSideFeatures.SYI_AI_POST,
                        'A'
                    );
            }
            Object.assign(this.data, data);
            const resp = (await fetchOrThrow(
                this.saveFetchURL,
                {
                    method: 'POST',
                    headers: { 'content-type': this.contentType },
                    body: JSON.stringify(this.data),
                    credentials: 'include',
                },
                {
                    serviceName: 'seller',
                    path: '/postad/:id',
                    method: 'POST',
                }
            ).then((x) => x.json())) as BaseSyiResponse;

            if (type === 'initialFetch') {
                await this.saveSuccess(resp);
            } else if (type === 'submitForm' || type === 'saveCategoryOnAttributeStep') {
                await trackPostedUpdatedRelisted(resp);
                await this.saveSuccess(resp);
            } else if (type === 'saveOnAttributeStep') {
                window.location.href = `${this.bffUrl}/postad/create?${new URLSearchParams({
                    categoryId: `${this.data.categoryId}`,
                    isFromAi: 'true',
                })}`;
            }
            return resp;
        } catch (e) {
            this.handleError(e);
            return undefined;
        }
    }

    /**
     * On POST success
     */
    async saveSuccess(response: BaseSyiResponse) {
        response.Helpers = helperFactory;
        this.attrs = Object.assign({}, this.attrs, response);
        if (this.attrs.redirectTo) {
            // expect relative path /postad/{id}/bumpup
            const draftId = this.postadURL.split('/postad/')[1];

            const flag = 'phoneverification';
            if (this.attrs.redirectTo.includes(flag)) {
                try {
                    const { adId, editorId } = await this.requestPhoneVerify(draftId);
                    const { appViewDevice } = dtlSyi.dispatch(getPageStateThunk()).page;

                    if (appViewDevice) {
                        // app will recognize the URL and show app phone verification page
                        this.navigateToVerifyInApp(adId, editorId);
                    } else {
                        const { listingDetails } = computeListingDetails();
                        listingDetails.id = adId;
                        dtlSyi.dispatch(
                            updateSyiPhoneVerification({
                                advertId: adId,
                                editorId,
                                listingDetails,
                                adCategoryId: response?.form?.categoryId || 0,
                                triggerPoint: 'post ad',
                            })
                        );
                    }
                } catch (error) {
                    console.error('catch and deal with the request error', error);
                }
                return;
            }

            // step1
            trackGA4Event<GA4.SyiPostAfter>({
                event: 'syi_post_after',
                ad_id: Number(this.attrs?.advertId),
                user_id: this.accountId,
                ad_status: postAdPageTypeToAdEventName()?.split('_')[1] ?? '',
                form_step: 'backend success',
                ...getTrackLogOnCookie(),
            });
            trackGA4Event({
                event: 'syi_complete_v2',
                ad_id: Number(this.attrs?.advertId),
                user_id: this.accountId,
                ad_status: postAdPageTypeToAdEventName()?.split('_')[1] ?? '',
                form_step: 'backend success',
            });
            window.location.href = `${this.bffBumpUpBaseUrl}?draftId=${draftId}`;
            return;
        }

        Object.assign(this.data, response.form);

        this.onBeforeLoadTemplates();

        dtlSyi.loadTemplates();
        dtlSyi.statusChange();
    }

    async requestPhoneVerify(draftId: string) {
        try {
            const url = `${this.bffPhoneVerificationUrl}/${draftId}`;
            const resp = await fetchOrThrow(url, {
                method: 'GET',
            }).then((x) => x.json());
            return resp;
        } catch (e) {
            this.handleError(e);
            return undefined;
        }
    }

    navigateToVerifyFinished(advertId: string, editorId: string) {
        const url = `${this.sellerUrl}/postad/${advertId}/${editorId}/verifyFinished`;
        window.location.href = url;
    }

    navigateToVerifyInApp(adId: string, editorId: string) {
        const url = `${this.bffBumpUpBaseUrl}?needPhoneVerify=true&adId=${adId}&editorId=${editorId}`;
        window.location.href = url;
    }

    onBeforeLoadTemplates() {
        const syiState = dtlSyi.dispatch(getPageStateThunk()).syi;

        // The selectedListingType is not persisted in the database, therefore we infer it when
        // the user refreshes the page.
        //
        // Post experiment, this will either be removed, or moved to the database and done properly.
        //
        // https://gumtree-uk.atlassian.net/browse/GTP-2970
        const urlParams = new URLSearchParams(window.location.search);
        const isFromAi = urlParams.get('isFromAi') === 'true';
        if (
            !syiState.selectedListingType &&
            (isFromAi || (this.attrs.panels.indexOf('ad-title') > -1 && this.attrs.form.title))
        ) {
            dtlSyi.dispatch(updateSyi({ selectedListingType: 'regular' }));
        }
    }

    async updateSellerSkills(categoryId: number) {
        const categorySkills = await this.initCategorySkills(categoryId);
        const accountCategorySkills = await this.initAccountCategorySkills(categoryId);
        dtlSyi.dispatch(
            updateSyi({
                categorySkills,
                ...accountCategorySkills,
            })
        );
    }

    async initCategorySkills(categoryId: number): Promise<Skill[]> {
        try {
            const url = `${this.sellerBaseUrl}/api/skill/attributes/${categoryId}`;
            const response = await fetchOrThrow(
                url,
                {
                    method: 'GET',
                    credentials: 'include',
                },
                {
                    serviceName: 'seller',
                    path: '/api/skill/attributes/:id',
                    method: 'GET',
                }
            );

            if (!response.ok) {
                throw new Error(`/skill/attributes error! Status: ${response.status}`);
            }

            const data = await response.json();
            const skills = data?.skillAttributes?.find(
                (item) => Number(item?.categoryId) === categoryId
            )?.skills;
            const result =
                skills?.map((skill) => ({
                    skillId: Number(skill.skillId),
                    content: skill.content,
                })) || [];

            return result;
        } catch (e) {
            console.error('fetch error', e);
            throw e;
        }
    }

    async initAccountCategorySkills(categoryId: number) {
        try {
            const response = await fetch(`${this.sellerBaseUrl}/api/skill/selected/${categoryId}`, {
                method: 'GET',
                credentials: 'include',
            });

            if (!response.ok) {
                throw new Error(`/skill/selected error! Status: ${response.status}`);
            }

            const data = await response.json();
            const result = {
                isEditedSkills: data.data?.edited || false,
                accountCategorySkills: data.data?.skills.map(Number) || [],
            };

            return result;
        } catch (e) {
            console.error('fetch error', e);
            throw e;
        }
    }

    async createSellerSkills(data: sellerSkillsParams) {
        try {
            const response = await fetch(`${this.sellerBaseUrl}/api/skill/create`, {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify(data),
                credentials: 'include',
            });

            if (!response.ok) {
                throw new Error(`/skill/create error! Status: ${response.status}`);
            }

            const result = await response.json();

            return result.data;
        } catch (e) {
            console.error('fetch error', e);
            throw e;
        }
    }

    async editSellerSkills(data: sellerSkillsParams) {
        try {
            const response = await fetch(`${this.sellerBaseUrl}/api/skill/update`, {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify(data),
                credentials: 'include',
            });

            if (!response.ok) {
                throw new Error(`/skill/update error! Status: ${response.status}`);
            }
            const result = await response.json();
            return result.data;
        } catch (e) {
            console.error('fetch error', e);
            throw e;
        }
    }

    async getTitleSuggestions(input: string) {
        try {
            const response = await fetch(
                `${this.sellerBaseUrl}/api/title/AIsuggest?input=${input}`,
                {
                    method: 'GET',
                    credentials: 'include',
                }
            );

            if (!response.ok) {
                throw new Error(`/api/title/AIsuggest error! Status: ${response.status}`);
            }

            const result = await response.json();
            return result;
        } catch (e) {
            console.error('fetch error', e);
        }
    }

    async getCategorySuggestions(input: string, type: string) {
        try {
            const response = await fetch(
                `${this.sellerBaseUrl}/api/category/AIsuggest?input=${input}&type=${type}`,
                {
                    method: 'GET',
                    credentials: 'include',
                }
            );

            if (!response.ok) {
                throw new Error(`/api/category/AIsuggest error! Status: ${response.status}`);
            }

            const result = await response.json();
            return result;
        } catch (e) {
            console.error('fetch error', e);
        }
        return { categories: [] };
    }

    async getFormAttributesSuggestions(
        input: string,
        secInput: string,
        type: string,
        categoryName: string
    ) {
        try {
            const response = await fetch(
                `${this.sellerBaseUrl}/api/categoryAttributes/AIsuggest?input=${input}&secInput=${secInput}&type=${type}&categoryName=${categoryName}`,
                {
                    method: 'GET',
                    credentials: 'include',
                }
            );

            if (!response.ok) {
                throw new Error(
                    `/api/categoryAttributes/AIsuggest error! Status: ${response.status}`
                );
            }

            const result = await response.json();
            return result;
        } catch (e) {
            console.error('fetch error', e);
        }
    }

    getSuggestionTitleAndDescParam(response) {
        const form = response.form;
        const categoryName =
            response.categoryCrumb?.find(({ id }) => form.categoryId === id)?.name ?? '';
        const images = response.postAdImages?.map(({ url }) => url).join(',') ?? '';
        const params = {
            title: form.title,
            categoryName: categoryName,
            attributes: form.attributes,
            images,
            desc: form.description,
        };
        return params;
    }

    async fetchSuggestionTitleAndDesc({
        title = '',
        categoryName = '',
        attributes = {},
        description = '',
        images = '',
    }) {
        const { postAdPageType, syiAiPostOnExperiment } = dtlSyi.dispatch(getPageStateThunk()).syi
            .context;
        if (postAdPageType !== 'create') {
            return;
        }

        let needToFetch = false;
        if (syiAiPostOnExperiment === GROWTHBOOK_VALUE_SYI_TITLE_FIRST && !description) {
            // need to fetch description
            needToFetch = true;
        } else if (
            syiAiPostOnExperiment === GROWTHBOOK_VALUE_SYI_PHOTO_FIRST &&
            (!title || !description)
        ) {
            // need to fetch title and description
            needToFetch ||= true;
        }
        if (!needToFetch) {
            return;
        }

        const params = {
            title,
            categoryName,
            attributes,
            images,
            desc: description,
        };
        return await this.getTitleAndDescSuggestions(params);
    }

    async getTitleAndDescSuggestions(params: TitleAndDescParams) {
        try {
            const paramsWithStringAttrs = {
                ...params,
                attributes: JSON.stringify(removeFalsyProperties(params.attributes)),
            };
            const urlEncodedData = new URLSearchParams(paramsWithStringAttrs).toString();

            const result = await fetchOrThrow(
                `${this.sellerBaseUrl}/api/titleAndDesc/AIsuggest`,
                {
                    method: 'POST',
                    headers: { 'content-type': 'application/x-www-form-urlencoded' },
                    body: urlEncodedData,
                    credentials: 'include',
                },
                {
                    serviceName: 'seller',
                    path: '/api/titleAndDesc/AIsuggest',
                    method: 'POST',
                }
            ).then((x) => x.json());

            return {
                title: result.title,
                description: result.desc,
            };
        } catch (e) {
            console.error('fetch error', e);
            return null;
        }
    }
}
export type TitleAndDescParams = {
    title: string;
    categoryName: string;
    attributes: Partial<Record<AttributeKey, string | null>>;
    images: string;
    desc: string;
};

export type CategoryCrumb = {
    children: boolean;
    childrenItems: SyiModelAttrs['categoryCrumb'];
    drilled: boolean;
    id: number;
    name: string;
    selected: boolean;
    seoName: string;
}[];

export type LocationCrumb = { id: number; name: string }[];

export interface SyiModelAttrs {
    advertId?: string;
    categoryCrumb?: CategoryCrumb;
    locationCrumb?: LocationCrumb;
    form: {
        title: string;
        categoryId: number;
        locationId?: number;
        postcode?: string;
        attributes: Record<string, any>;
        formErrors: Record<string, any>;
    };
    panels: {
        length?: number;
    }[];
    postAdImages?: PostAdImageDef[];
    pricingMetadata: PricingMetadata;
    redirectTo?: string;
    sellerType: null | SyiSellerType;
    status: SyiModelStatus;
    totalPrice?: number;
}

export interface PostAdImageDef {
    id: number;
    size: null;
    thumbnailUrl: string;
    url: string;
}

interface sellerSkillsParams {
    categoryId: string;
    selectedSkills: string[];
}

async function trackPostedUpdatedRelisted({ form: data, redirectTo }: BaseSyiResponse) {
    try {
        const { postAdPageType } = dtlSyi.dispatch(getPageStateThunk()).syi.context;
        const eventName = postAdPageTypeToAdEventName();

        if (!eventName) {
            return; // category page
        }

        const errors = data.formErrors ?? {};
        const errorKVPairs = Object.entries(errors).reduce((agg, [key, texts]) => {
            texts.forEach((value) => agg.push({ key, value }));
            return agg;
        }, [] as { key: string; value: string }[]);

        if (redirectTo) {
            trackGA4PreNav<GA4.PostAdEvent>({
                event: eventName,
                formName: 'syi-form',
                formStep: 'submit',
                formValidation: 'success',
                ...computeListingDetails(),
                ...(postAdPageType === 'edit' &&
                    eventName === 'ad_posted_attempt' && {
                        adFieldsUpdated: detectFormDataChanges(),
                    }),
            });
        } else if (errorKVPairs.length) {
            trackGA4Event<GA4.PostAdEvent>({
                event: eventName,
                formName: 'syi-form',
                formStep: 'submit',
                formValidation: 'failure',
                formErrors: errorKVPairs.map(({ key, value }) =>
                    JSON.stringify(`${key}: ${value}`).replace(/,/g, '')
                ),
                ...computeListingDetails(),
            });
        }
    } catch (e) {
        console.error('trackPostedUpdatedRelisted', e);
    }
}

export function postAdPageTypeToAdEventName(
    progression?: boolean
): GA4.PostAdEvent['event'] | null {
    const { postAdPageType } = dtlSyi.dispatch(getPageStateThunk()).syi.context;
    return (
        (postAdPageType === 'create' &&
            (progression ? 'ad_posted_form_progression' : 'ad_posted_attempt')) ||
        (postAdPageType === 'edit' &&
            (progression ? 'ad_updated_form_progression' : 'ad_updated_attempt')) ||
        (postAdPageType === 'repost' &&
            (progression ? 'ad_relisted_form_progression' : 'ad_relisted_attempt')) ||
        null
    );
}

export function computeListingDetails(): GA4.BaseListingDetails {
    try {
        const {
            data,
            attrs: { advertId, locationCrumb, categoryCrumb, sellerType },
        } = dtlSyi.model;

        const { totalPrice, priceSansFeatures, selectedProductNames } = computePriceSansFeatures();
        const categoryLength = categoryCrumb?.length ?? 1;
        const category = categoryCrumb?.[categoryLength - 1];

        const trackLog = getCookieValue(tracklogKeyOnSyi);
        return {
            listingDetails: {
                age: undefined,
                contactEmail: !!data.contactEmail && data.useEmail,
                contactNumber: !!data.contactTelephone && data.usePhone,
                type: totalPrice > 0 ? 'paid' : 'free',
                cost: priceSansFeatures,
                totalCost: totalPrice,
                id: Number(advertId),
                isTrade: sellerType?.sellerType === 'trade',
                location: locationCrumb?.at(-1)?.name || undefined,
                locationId: locationCrumb?.at(-1)?.id,
                locationHierarchy: locationCrumb?.length
                    ? `L${locationCrumb.length - 1}`
                    : undefined,
                locationShowOnMap: !!data.visibleOnMap,
                name: data.title ?? '',
                numberOfImages: data.imageIds?.length ?? 0,
                numberWordsDescription: data.description?.trim().split(/\s+/).length ?? -1,
                price: data.attributes?.price ? Number(data.attributes.price) : undefined,
                pricePennies: data.attributes?.price
                    ? Number(data.attributes?.price) * 100
                    : undefined,
                promotions: selectedProductNames,
                category: category?.name ?? '',
                categoryId: category?.id ?? -1,
                subcategory1: categoryCrumb?.[0]?.id,
                subcategory2: categoryCrumb?.[1]?.id,
                subcategory3: categoryCrumb?.[2]?.id,
                subcategory4: categoryCrumb?.[3]?.id,
                subcategory5: categoryCrumb?.[4]?.id,
                videoLink: !!data.youtubeLink,
                websiteLink: !!data.websiteUrl && data.useUrl,
                dogBreed: undefined,
                isFromMotorsFeed: undefined,
            },
            requestId: trackLog?.requestId ?? '',
            draftId: trackLog?.draftId ?? '',
        };
    } catch (e) {
        console.error('computeListingDetails', e);
        return {
            listingDetails: {},
            listingDetailsError: `${e?.stack ?? e}`,
        };
    }
}

function computePriceSansFeatures() {
    const { totalPrice } = dtlSyi.dispatch(getPageStateThunk()).syi;
    const { pricingMetadata } = dtlSyi.model.attrs;

    if (totalPrice) {
        let priceSansFeatures = totalPrice;

        const selectedProductNames = Object.values(dtlSyi.model.data.features)
            .filter(({ selected }) => selected)
            .map(({ productName }) => productName);

        if (selectedProductNames.length) {
            const prodNameToPrice = Object.values(pricingMetadata.featureOptions).reduce(
                (agg, { prices }) => {
                    prices?.forEach(({ productName, price }) => (agg[productName] = price));
                    return agg;
                },
                {} as Record<string, number>
            );
            selectedProductNames.forEach(
                (productName) => (priceSansFeatures -= prodNameToPrice[productName] ?? 0)
            );
        }

        return { totalPrice, priceSansFeatures, selectedProductNames };
    } else {
        return { totalPrice: 0, priceSansFeatures: 0, selectedProductNames: [] };
    }
}

function detectFormDataChanges(): (keyof PanelFormData | AttributeKey | FeatureType)[] {
    const { initialData: pre, data } = dtlSyi.model;

    const { attributes, features, formErrors: _, removeDraft: __, ...post } = data;
    post.categoryId = Number(post.categoryId);
    post.locationId = Number(post.locationId);
    post.mainImageId = Number(post.mainImageId) || null; // "0" -> null
    ['images-file-input', 'shouldByPassWebsiteLinkPayment', 'submitForm'].forEach(
        (key) => delete post[key]
    );

    return [
        ...keys(post).filter((key) => JSON.stringify(pre[key]) !== JSON.stringify(post[key])),
        ...keys(attributes).filter((key) => pre.attributes[key] !== attributes[key]),
        // Cannot deselect feature when updating advert
        ...keys(features).filter(
            (key) => pre.features[key]?.selected !== true && features[key]?.selected === true
        ),
    ];
}
